import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';

import {
  GoalsService,
  UserObjectiveStatus,
  UserObjectivesGridRequest,
  UserObjectivesGridResponseData,
  UserObjectivesProgressBucket,
  UserTags,
} from '../../../services/goals.service';

/**
 * Admin listing of per-user objective assignments with their cached progress snapshot, and a
 * per-row Retire action that expires ONE userObjective directly — no cascade, distinct from
 * objectives-catalog.component.ts which retires the shared catalog doc. Column filters/search
 * dropdowns mirror partner/manage/manage.component.html's nz-filter-trigger pattern rather than a
 * flat toolbar of inputs.
 */
@Component({
  selector: 'app-user-objectives-list',
  templateUrl: './user-objectives-list.component.html',
})
export class UserObjectivesListComponent extends AnyoComponent {
  constructor(
    private goalsSvc: GoalsService,
    auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toastrService: ToastService,
    private modal: NzModalService,
  ) {
    super(auth);
  }

  UserObjectiveStatus = UserObjectiveStatus;
  UserObjectivesProgressBucket = UserObjectivesProgressBucket;
  statusOptions = Object.values(UserObjectiveStatus);
  progressBucketOptions = Object.values(UserObjectivesProgressBucket);
  userTagsOptions = Object.values(UserTags);

  tableLoading = false;
  page = 1;
  pageSize = 20;
  totalRecords = 0;
  pageSizeOptions = [20, 50, 100];
  data: UserObjectivesGridResponseData[] = [];

  // Column search dropdowns (regex, text input + Ok/Reset).
  expertTitleSearch = '';
  displayTitleSearch = '';
  userEmailSearch = '';
  partnerIdSearch = '';
  // Internal-only — set by the objectives-catalog "View Users" drill-down link via the URL, never
  // exposed as a text input of its own in this grid.
  objectiveIdFilter = '';

  // Column filter dropdowns (checkbox multi-select).
  statusFilterValues: UserObjectiveStatus[] = [];
  userTagsFilterValues: string[] = [];
  progressBucketFilterValues: UserObjectivesProgressBucket[] = [];

  // Default OFF — test users are excluded unless an admin explicitly opts in to see them.
  includeTestUsers = false;

  private fetchRequestId = 0;

  async ready() {
    if (!this.user) return;
    const restored = this.restoreQueryFromUrl();
    if (restored) this.applyRequestToState(restored);
    this.fetch();
  }

  buildRequest(exportFlag = false): UserObjectivesGridRequest {
    const req: UserObjectivesGridRequest = {
      page: this.page,
      pageSize: this.pageSize,
      export: exportFlag,
      filters: {
        ...(this.statusFilterValues.length
          ? { STATUS: this.statusFilterValues }
          : {}),
        ...(this.userTagsFilterValues.length
          ? { USER_TAGS: this.userTagsFilterValues }
          : {}),
        ...(this.progressBucketFilterValues.length
          ? { PROGRESS_BUCKET: this.progressBucketFilterValues }
          : {}),
        EXCLUDE_TEST_USERS: [this.includeTestUsers ? 'false' : 'true'],
      },
    };
    const searchQuery: UserObjectivesGridRequest['searchQuery'] = {};
    if (this.objectiveIdFilter.trim())
      searchQuery.OBJECTIVE_ID = this.objectiveIdFilter.trim();
    if (this.expertTitleSearch.trim())
      searchQuery.EXPERT_TITLE = this.expertTitleSearch.trim();
    if (this.displayTitleSearch.trim())
      searchQuery.DISPLAY_TITLE = this.displayTitleSearch.trim();
    if (this.userEmailSearch.trim())
      searchQuery.USER_EMAIL = this.userEmailSearch.trim();
    if (this.partnerIdSearch.trim())
      searchQuery.PARTNER_ID = this.partnerIdSearch.trim();
    if (Object.keys(searchQuery).length) req.searchQuery = searchQuery;
    return req;
  }

  fetch(): void {
    this.tableLoading = true;
    const req = this.buildRequest(false);
    this.updateUrlWithQuery(req);
    const requestId = ++this.fetchRequestId;
    this.goalsSvc.getUserObjectivesGrid(req).subscribe({
      next: (res) => {
        if (requestId !== this.fetchRequestId) return;
        this.data = res.data;
        this.totalRecords = res.totalRecords;
        this.pageSize = res.pageSize;
        this.tableLoading = false;
      },
      error: (err) => {
        if (requestId !== this.fetchRequestId) return;
        this.tableLoading = false;
        const errorBody = err.error as IAnyoError;
        this.toastrService.showAnyoErrorToast(
          errorBody?.description ?? 'Failed to load user objectives',
        );
      },
    });
  }

  /** Bound to every column dropdown's own "Ok" button — ngModel already keeps state in sync as the
   * admin types/checks, so this just re-pages and re-fetches, matching manage.component.ts's own
   * per-column search functions which do the same after their own field assignment. */
  applyFilters(): void {
    this.page = 1;
    this.fetch();
  }

  clearFilters(): void {
    this.expertTitleSearch = '';
    this.displayTitleSearch = '';
    this.userEmailSearch = '';
    this.partnerIdSearch = '';
    this.objectiveIdFilter = '';
    this.statusFilterValues = [];
    this.userTagsFilterValues = [];
    this.progressBucketFilterValues = [];
    this.includeTestUsers = false;
    this.page = 1;
    this.fetch();
  }

  nzPageIndexChange(p: number): void {
    this.page = p;
    this.fetch();
  }

  nzPageSizeChange(s: number): void {
    this.pageSize = s;
    this.fetch();
  }

  onStatusFilterChange(values: string[]): void {
    this.statusFilterValues = values as UserObjectiveStatus[];
  }

  onUserTagsFilterChange(values: string[]): void {
    this.userTagsFilterValues = values;
  }

  onProgressBucketFilterChange(values: string[]): void {
    this.progressBucketFilterValues = values as UserObjectivesProgressBucket[];
  }

  canRetire(row: UserObjectivesGridResponseData): boolean {
    return (
      row.status !== UserObjectiveStatus.COMPLETED &&
      row.status !== UserObjectiveStatus.EXPIRED
    );
  }

  retireAlert(row: UserObjectivesGridResponseData): void {
    this.modal.confirm({
      nzTitle: `Retire "${row.displayTitle || row.objectiveId}"?`,
      nzContent: `This expires this one user's goal assignment. It will not affect any other user.`,
      nzOnOk: async () => {
        this.goalsSvc.retireUserObjective(row.userObjectiveId).subscribe({
          next: () => {
            this.toastrService.showSuccess('User objective retired');
            this.fetch();
          },
          error: (err) => {
            const errorBody = err.error as IAnyoError;
            this.toastrService.showAnyoErrorToast(
              errorBody?.description ?? 'Failed to retire user objective',
            );
          },
        });
      },
      nzOkText: 'Retire',
      nzCancelText: 'Cancel',
      nzClosable: false,
    });
  }

  private updateUrlWithQuery(req: UserObjectivesGridRequest): void {
    const encoded = encodeURIComponent(JSON.stringify(req));
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { gridQuery: encoded },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private restoreQueryFromUrl(): UserObjectivesGridRequest | null {
    const qp = this.route.snapshot.queryParamMap.get('gridQuery');
    if (!qp) return null;
    try {
      const parsed: unknown = JSON.parse(decodeURIComponent(qp));
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return null;
      }
      return parsed as UserObjectivesGridRequest;
    } catch {
      return null;
    }
  }

  private applyRequestToState(req: UserObjectivesGridRequest): void {
    if (typeof req.page === 'number') this.page = req.page;
    if (typeof req.pageSize === 'number') this.pageSize = req.pageSize;
    if (req.searchQuery?.OBJECTIVE_ID)
      this.objectiveIdFilter = req.searchQuery.OBJECTIVE_ID;
    if (req.searchQuery?.EXPERT_TITLE)
      this.expertTitleSearch = req.searchQuery.EXPERT_TITLE;
    if (req.searchQuery?.DISPLAY_TITLE)
      this.displayTitleSearch = req.searchQuery.DISPLAY_TITLE;
    if (req.searchQuery?.USER_EMAIL)
      this.userEmailSearch = req.searchQuery.USER_EMAIL;
    if (req.searchQuery?.PARTNER_ID)
      this.partnerIdSearch = req.searchQuery.PARTNER_ID;
    this.statusFilterValues = (req.filters?.STATUS as UserObjectiveStatus[]) ?? [];
    this.userTagsFilterValues = req.filters?.USER_TAGS ?? [];
    this.progressBucketFilterValues =
      (req.filters?.PROGRESS_BUCKET as UserObjectivesProgressBucket[]) ?? [];
    this.includeTestUsers = req.filters?.EXCLUDE_TEST_USERS?.[0] === 'false';
  }
}
